import copy from "copy-to-clipboard";
import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { BiQr, BiSolidChat } from "react-icons/bi";
import { QRCode } from "react-qrcode-logo";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "../../app/hook";
import PopoverWithAudio, {
  IPopoverWithAudioType
} from "../../components/common/PopoverWithAudio";
import { AlertsType } from "../../constants/alerts.constant";
import { useAsExtensionAudioLinks } from "../../constants/dashboard-popover-urls.constant";
import { MultiLanguageUrl } from "../../constants/multiLanguage.constant";
import usePermission from "../../hooks/usePermission";
import { botsUiState, handleToggleCloneShareModal } from "./botSlice";
import { BsX } from "react-icons/bs";

function useImageToBase64(url: string) {
  const [base64Image, setBase64Image] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        setLoading(true);
        const response = await fetch(url);
        const blob = await response.blob();

        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error: any) {
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    };

    fetchImage().then(setBase64Image);
  }, [url]);

  return { base64Image, loading, error };
}

const CloneShareModal = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const permissions = usePermission();
  const { showCloneShareModal, cloneShareModalData } =
    useAppSelector(botsUiState);
  const { base64Image, loading, error } = useImageToBase64(
    permissions.extensionPage.qrcodeAvatarAccess
      ? cloneShareModalData.logo
      : "https://cdn.1cdn.app/application/LOUISEBOT/23091707385795_Logo_colorful.svg"
  );

  const useAsExtensionPopData: IPopoverWithAudioType = {
    audioUrl: useAsExtensionAudioLinks[i18n.language],
    contentText: t("WidgetContent/pop-03")
  };

  const ref = useRef<QRCode>();

  const [state, setState] = useState<{ [key: string]: any }>({
    enableCORS: true,
    size: "200",
    qrStyle: "squares",
    quietZone: "22",
    logoPadding: "5",
    logoPaddingStyle: "circle",
    bgColor: "#FFFFFF"
  });

  const handleChatLink = () => {
    if (cloneShareModalData?.cloneId) {
      const url = `${import.meta.env.VITE_UI_ADDRESS}/${
        MultiLanguageUrl[i18n.language] || "en-US"
      }/chat/${cloneShareModalData?.cloneId}`;

      copy(url);

      toast.success(t(AlertsType.ISSCKO002));

      // navigator.clipboard.writeText(url);
    }
  };

  const handleChange = ({ key, value }: any) => {
    setState((prevState) => ({ ...prevState, [key]: value }));
  };

  const handleDownloadQrCode = () => {
    ref.current?.download();
  };

  useEffect(() => {
    handleChange({
      key: "value",
      value: `${import.meta.env.VITE_UI_ADDRESS}/en-US/chat/${
        cloneShareModalData?.cloneId
      }`
    });

    if (!loading && base64Image) {
      handleChange({ key: "logoImage", value: base64Image });
    }

    if (error) {
      handleChange({ key: "logoImage", value: "/logo.png" });
    }
  }, [
    showCloneShareModal,
    cloneShareModalData?.cloneId,
    loading,
    base64Image,
    error
  ]);

  return (
    <Modal
      show={showCloneShareModal}
      centered
      onHide={() => dispatch(handleToggleCloneShareModal({ status: false }))}
      className="use-assistant-modal p-0"
      size="lg"
    >
      <div className="lg:tw-h-96 tw-shadow-[0px_4px_6px_-2px_rgba(0,0,0,0.05)] tw-shadow-lg tw-rounded-2xl tw-overflow-hidden tw-justify-start tw-items-center tw-col lg:tw-inline-flex">
        <div className="tw-w-full lg:tw-w-96 lg:!tw-h-96 tw-px-6 lg:tw-py-8 tw-bg-gray-50 lg:tw-rounded-tl-2xl lg:tw-rounded-bl-2xl tw-flex-col tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
          <div
            role="button"
            onClick={() =>
              dispatch(handleToggleCloneShareModal({ status: false }))
            }
            className="tw-self-end tw-block lg:tw-hidden tw-justify-end tw-items-center tw-mt-2 tw-bg-gray-50"
          >
            <BsX className="tw-text-gray-400 tw-w-6 tw-h-6" />
          </div>
          <div className="lg:tw-w-96 lg:tw-h-96 tw-relative">
            {loading ? (
              <Spinner size="sm" />
            ) : (
              <div className="tw-w-full tw-h-full tw-gap-2.5">
                <QRCode
                  style={{
                    width: "100%",
                    height: "100%"
                  }}
                  ref={ref as MutableRefObject<QRCode>}
                  {...{
                    ...state,
                    eyeRadius: [
                      // build eyeRadius manually
                      {
                        outer: [
                          state.eyeradius_0_outer_0,
                          state.eyeradius_0_outer_1,
                          state.eyeradius_0_outer_2,
                          state.eyeradius_0_outer_3
                        ],
                        inner: [
                          state.eyeradius_0_inner_0,
                          state.eyeradius_0_inner_1,
                          state.eyeradius_0_inner_2,
                          state.eyeradius_0_inner_3
                        ]
                      },
                      {
                        outer: [
                          state.eyeradius_1_outer_0,
                          state.eyeradius_1_outer_1,
                          state.eyeradius_1_outer_2,
                          state.eyeradius_1_outer_3
                        ],
                        inner: [
                          state.eyeradius_1_inner_0,
                          state.eyeradius_1_inner_1,
                          state.eyeradius_1_inner_2,
                          state.eyeradius_1_inner_3
                        ]
                      },
                      {
                        outer: [
                          state.eyeradius_2_outer_0,
                          state.eyeradius_2_outer_1,
                          state.eyeradius_2_outer_2,
                          state.eyeradius_2_outer_3
                        ],
                        inner: [
                          state.eyeradius_2_inner_0,
                          state.eyeradius_2_inner_1,
                          state.eyeradius_2_inner_2,
                          state.eyeradius_2_inner_3
                        ]
                      }
                    ],
                    eyeColor: [
                      // build eyeColor manually
                      {
                        outer:
                          state.eyecolor_0_outer ?? state.fgColor ?? "#000000",
                        inner:
                          state.eyecolor_0_inner ?? state.fgColor ?? "#000000"
                      },
                      {
                        outer:
                          state.eyecolor_1_outer ?? state.fgColor ?? "#000000",
                        inner:
                          state.eyecolor_1_inner ?? state.fgColor ?? "#000000"
                      },
                      {
                        outer:
                          state.eyecolor_2_outer ?? state.fgColor ?? "#000000",
                        inner:
                          state.eyecolor_2_inner ?? state.fgColor ?? "#000000"
                      }
                    ]
                  }}
                />
              </div>
            )}
          </div>
        </div>
        <div className="lg:tw-w-96 lg:!tw-h-96 tw-px-6 lg:tw-x-0  lg:tw-pl-12 lg:tw-pr-0 tw-py-3 tw-bg-white lg:tw-rounded-tr-2xl lg:tw-rounded-br-2xl tw-justify-start tw-items-start tw-gap-2.5 tw-flex">
          <div className="tw-pr-3 tw-grow tw-shrink tw-basis-0 tw-self-stretch tw-py-5 tw-flex-col tw-justify-between tw-items-start tw-inline-flex">
            <div className="tw-self-stretch tw-grow tw-shrink tw-basis-0 tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
              <div className="tw-self-stretch tw-grow tw-shrink tw-basis-0 tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex">
                <div className="lg:tw-h-32 tw-flex-col tw-justify-start tw-items-start tw-gap-9 tw-flex">
                  <div className="tw-self-stretch lg:tw-h-32 tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
                    <div className="tw-self-stretch tw-justify-start tw-items-center tw-gap-2 tw-inline-flex">
                      <div className="tw-grow tw-shrink tw-basis-0 tw-text-gray-700 tw-text-3xl tw-font-semibold tw-font-['Lexend'] tw-leading-9">
                        {t("ExtensionContent/title")}
                        <PopoverWithAudio data={useAsExtensionPopData} />
                      </div>
                    </div>
                    <div className="tw-self-stretch tw-text-gray-500 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                      {t("ExtensionContent/text-01")}
                    </div>
                  </div>
                </div>
                <div className="tw-self-stretch tw-h-24 tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-flex tw-mt-4 lg:tw-mt-6">
                  <button
                    onClick={handleDownloadQrCode}
                    disabled={loading}
                    className="tw-self-stretch tw-pl-2.5 tw-pr-4 tw-py-2 tw-bg-white tw-rounded-full tw-shadow-sm tw-border tw-border-gray-300 tw-justify-center tw-items-center tw-gap-2 tw-inline-flex tw-overflow-hidden  tw-border-solid"
                  >
                    <BiQr className="tw-text-gray-400 tw-w-4 tw-h-4 tw-relative tw-overflow-hidden" />
                    <div className="tw-text-gray-700 tw-text-sm tw-font-medium tw-font-['Lexend'] tw-leading-tight">
                      {loading ? (
                        <Spinner size="sm" />
                      ) : (
                        t("ExtensionContent/list-02")
                      )}
                    </div>
                  </button>
                  <button
                    onClick={handleChatLink}
                    className="tw-self-stretch tw-pl-2.5 tw-pr-4 tw-pt-2 tw-pb-1.5 tw-bg-white tw-rounded-full tw-shadow-sm tw-border tw-border-gray-300 tw-justify-center tw-items-center tw-gap-2 tw-inline-flex tw-overflow-hidden  tw-border-solid"
                  >
                    <BiSolidChat className="tw-w-4 tw-h-4 tw-relative tw-overflow-hidden tw-text-gray-400" />
                    <div className="tw-text-gray-700 tw-text-sm tw-font-medium tw-font-['Lexend'] tw-leading-tight">
                      {t("ExtensionContent/list-01")}
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <BsX
            role="button"
            onClick={() =>
              dispatch(handleToggleCloneShareModal({ status: false }))
            }
            className="tw-hidden lg:tw-block tw-text-gray-400 tw-w-6 tw-h-6 tw-relative tw-overflow-hidden"
          />
        </div>
      </div>
    </Modal>
  );
};

export default CloneShareModal;
